import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/app/common/common-components/ui/Alert";
import { Link } from "@/app/common/common-components/ui/Link";
import { cn } from "@/app/common/common-functions/functions";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { AlertCircleIcon, ExternalLinkIcon } from "lucide-react";
import moment from "moment";

const getIncidents = async () => {
  const data = await axios.get<
    {
      incidentID: string;
      name: string;
      description: string;
      status: string;
    }[]
  >("https://status.entegral.net/api/incidents");
  return data.data;
};

const getIncidentDetails = async (incidentID: string) => {
  const data = await axios.get<{
    incidentID: string;
    name: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    incidentUpdates?: {
      incidentID: string;
      incidentUpdateID: string;
      incidentStatus: string;
      message: string;
      createdAt: string;
      updatedAt: string;
    }[];
  }>("https://status.entegral.net/api/incidents/" + incidentID);
  return data.data;
};

type StatusPageFeedProps = {
  className?: string;
  keywords?: string[];
  maxIncidents?: number;
  maxUpdates?: number;
};

const StatusPageFeed = ({
  className,
  keywords,
  maxIncidents = 2,
  maxUpdates = 2,
}: StatusPageFeedProps) => {
  const { data: incidents } = useQuery({
    queryKey: ["incidents", keywords],
    queryFn: async () => {
      const allIncidents = await getIncidents();
      const unresolvedIncidents = allIncidents.filter(
        (incident) => incident.status !== "Resolved"
      );

      const unresolvedIncidentsWithDate = await Promise.all(
        unresolvedIncidents.map(
          async (incident) => await getIncidentDetails(incident.incidentID)
        )
      );

      const filteredIncidents = unresolvedIncidentsWithDate.filter(
        (incident) =>
          !!keywords
            ? keywords.some((keyword) =>
                incident.name.toLowerCase().includes(keyword.toLowerCase())
              )
            : true
      );

      return filteredIncidents.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    },
  });

  if (!incidents || incidents.length === 0) {
    return null;
  }

  return (
    <div className={cn(className, "tw-flex tw-flex-col tw-items-end tw-gap-1")}>
      {incidents.slice(0, maxIncidents).map((incident) => (
        <Alert className="[&>svg]:tw-text-destructive">
          <AlertCircleIcon className="tw-size-4" />
          <AlertTitle>{incident.name}</AlertTitle>
          <div className="tw-flex tw-flex-col tw-gap-4">
            {incident.incidentUpdates
              ?.filter((update) => update.message !== "")
              ?.slice(0, maxUpdates)
              ?.sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
              )
              .map((update) => (
                <AlertDescription className="tw-flex tw-flex-col">
                  <div>{update.message}</div>
                  <span className="tw-text-xs tw-text-muted-foreground">
                    {moment(update.createdAt).format("DD MMMM @ HH:mm")}
                  </span>
                </AlertDescription>
              ))}
          </div>
        </Alert>
      ))}
      <Link
        to="https://status.entegral.net"
        target="_blank"
        className="tw-flex tw-items-center tw-gap-2"
      >
        View all incidents
        <ExternalLinkIcon className="tw-size-4" />
      </Link>
    </div>
  );
};

export default StatusPageFeed;
